import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import PageHeader from "@/components/feature/PageHeader";
import Reveal from "@/components/base/Reveal";
import { profile } from "@/mocks/profile";
import { works as worksData } from "@/mocks/works";
import type { Work } from "@/types/content";

const works = worksData as Work[];

const infoItems = [
  { icon: "ri-user-3-line", label: "姓名", value: profile.name },
  { icon: "ri-translate-2", label: "罗马音", value: profile.romanized },
  { icon: "ri-cake-2-line", label: "生日", value: profile.birthday },
  { icon: "ri-map-pin-line", label: "出生地", value: profile.birthplace },
  { icon: "ri-ruler-line", label: "身高", value: profile.height },
  { icon: "ri-heart-pulse-line", label: "血型", value: profile.bloodType },
  { icon: "ri-body-scan-line", label: "三围", value: profile.measurements },
  { icon: "ri-building-2-line", label: "所属事务所", value: profile.agency },
  { icon: "ri-award-line", label: "出道", value: profile.debut },
  { icon: "ri-global-line", label: "语言", value: profile.languages },
];

export default function Profile() {
  const representative = profile.representativeWorkIds
    .map((id) => works.find((w) => w.id === id))
    .filter((w): w is Work => Boolean(w));

  return (
    <div className="w-full min-h-screen bg-background-50">
      <title>奥田咲个人资料 - 生日、身高与代表作品</title>
      <meta
        name="description"
        content="奥田咲完整个人资料页，包含姓名、生日、出生地、身高、所属事务所、社交媒体等基本信息，以及职业生涯介绍与代表作品导航。"
      />
      <meta name="keywords" content="奥田咲资料,奥田咲个人资料,奥田咲身高,奥田咲生日,日本女演员" />

      <SiteHeader />
      <PageHeader
        eyebrow="PROFILE"
        title="个人资料"
        description="了解奥田咲的基本信息、职业生涯与代表作品。以下资料均为演示用途整理，如有出入请以官方发布为准。"
        crumbs={[{ label: "首页", to: "/" }, { label: "个人资料" }]}
      />

      <main className="w-full px-4 md:px-8 py-10 md:py-14">
        <div className="w-full max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-4">
              <Reveal>
                <div className="relative w-full max-w-sm mx-auto lg:mx-0 aspect-[4/5] rounded-lg overflow-hidden bg-background-200 border border-background-200">
                  <img
                    src={profile.photo}
                    alt="奥田咲个人资料形象照"
                    title="奥田咲 个人资料"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </Reveal>

              <Reveal className="mt-5 max-w-sm mx-auto lg:mx-0" delay={80}>
                <div className="p-5 rounded-lg bg-background-100 border border-background-200">
                  <h3 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2">
                    <i className="ri-share-line text-accent-600"></i>
                    社交媒体
                  </h3>
                  <ul className="mt-4 space-y-2.5">
                    {profile.socials.map((s) => (
                      <li key={s.name}>
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noreferrer"
                          className="group flex items-center gap-3 p-3 rounded-md bg-background-50 border border-background-200 hover:border-primary-300 transition-colors"
                        >
                          <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
                            <i className={`${s.icon} text-lg`}></i>
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium text-foreground-900">
                              {s.name}
                            </span>
                            <span className="block text-xs text-foreground-500 truncate">
                              {s.handle}
                            </span>
                          </span>
                          <i className="ri-arrow-right-up-line text-foreground-400 group-hover:text-primary-600 transition-colors"></i>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-8">
              <Reveal>
                <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] text-accent-700">
                  <span className="w-6 h-0.5 bg-accent-500"></span>
                  BASIC INFO
                </span>
                <h1 className="mt-3 font-heading text-3xl md:text-5xl font-bold text-foreground-950">
                  {profile.name}
                </h1>
                <p className="mt-2 text-sm tracking-[0.24em] text-foreground-500">
                  {profile.romanized}
                </p>
              </Reveal>

              <Reveal delay={80}>
                <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
                  {profile.careerStats.map((stat) => (
                    <div
                      key={stat.label}
                      className="p-4 rounded-lg bg-background-100 border border-background-200 text-center"
                    >
                      <p className="font-heading text-xl md:text-2xl font-bold text-primary-700">
                        {stat.value}
                      </p>
                      <p className="mt-1.5 text-xs text-foreground-500">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </Reveal>

              <Reveal delay={120}>
                <div className="mt-8 p-6 rounded-lg bg-background-50 border border-background-200">
                  <h2 className="font-heading text-lg font-bold text-foreground-950 flex items-center gap-2">
                    <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                    基本资料
                  </h2>
                  <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-0">
                    {infoItems.map((item) => (
                      <div
                        key={item.label}
                        className="flex items-center justify-between gap-4 py-3 border-b border-background-200"
                      >
                        <dt className="flex items-center gap-2 text-sm text-foreground-500 whitespace-nowrap">
                          <i className={`${item.icon} text-foreground-400`}></i>
                          {item.label}
                        </dt>
                        <dd className="text-sm font-medium text-foreground-900 text-right break-words">
                          {item.value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                  <div className="mt-5 pt-5 border-t border-background-200">
                    <p className="text-sm text-foreground-500 mb-3">兴趣爱好</p>
                    <div className="flex flex-wrap gap-2">
                      {profile.hobbies.map((h) => (
                        <span
                          key={h}
                          className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium"
                        >
                          {h}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>

              <Reveal delay={160}>
                <div className="mt-8">
                  <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 flex items-center gap-2">
                    <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                    职业生涯
                  </h2>
                  <div className="mt-5 space-y-5">
                    {profile.bio.map((paragraph, idx) => (
                      <p
                        key={`bio-${idx}`}
                        className="text-sm md:text-base text-foreground-700 leading-9"
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>

          <div className="mt-16 md:mt-20">
            <div className="flex items-end justify-between gap-4 mb-6">
              <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950">
                代表作品
              </h2>
              <Link
                to="/works"
                className="text-sm text-primary-700 hover:text-accent-600 transition-colors whitespace-nowrap"
              >
                全部作品
                <i className="ri-arrow-right-s-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {representative.map((work, idx) => (
                <Reveal key={work.id} delay={idx * 50}>
                  <Link
                    to={`/works/${work.id}`}
                    className="group flex gap-4 p-3 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer h-full"
                  >
                    <div className="w-20 shrink-0 aspect-[3/4] rounded-md overflow-hidden bg-background-200">
                      <img
                        src={work.cover}
                        alt={`${work.title} - 奥田咲代表作品`}
                        title={`${work.title} 奥田咲`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="min-w-0 flex flex-col justify-center">
                      <span className="text-xs text-accent-700 font-medium">{work.type}</span>
                      <h3 className="mt-1 font-heading text-base font-bold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
                        {work.title}
                      </h3>
                      <p className="mt-1.5 text-xs text-foreground-500">
                        {work.year} · {work.role}
                      </p>
                      <span className="mt-2 inline-flex items-center gap-1 text-xs text-primary-700">
                        查看详情
                        <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform"></i>
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}